//import axios from 'axios';
import { isNil } from 'lodash';

/**
 * Interface to whatever storage method the environment needs 
 */
const storage = {
  get: (key) => window.sessionStorage.getItem(key),
  set: (key, val) => window.sessionStorage.setItem(key, val),
  remove: (key) => window.sessionStorage.removeItem(key),
};

/**
 * The browser `storage` key to persist sessions under
 * @type {String}
 */
export const SESSION_STORAGE_KEY = 'Ⅲ_session';
/**
 * The Authentication host (ie pingFederate)
 * @type {String}
 */
export const AUTH_HOST = '';
/**
 * The `token_key` as given via redirect by auth provider
 * @type {String}
 */
const TOKEN_KEY = 'jwt';

/**
 * The `error_key` as given via redirect by auth provider
 * @type {String}
 */
const ERROR_KEY = 'error';

/**
 * The key to record the last activity time for localStorage.
 */
const SESSION_LAST_ACTIVITY = '_la';

const { protocol, hostname, port, pathname } = window.location;

/**
 * TODO: OpenID configuration
 */
export const config = {
  redirect_uri: `${protocol}//${hostname}${port ? `:${port}` : ''}${pathname}`,
  client_id: 'Ⅲ',
  state: 'login',
  response_type: 'id_token token',
  scope: 'openid profile',
};

/**
 * Parse the given URL `hashValue` for an access_token
 * @function
 * @param  {String} hashValue - `window.location.hash`
 * @return {Boolean}          - Is there an OpenID-compliant JWT serialized in URL hash
 */
export const isAuthHashValuePresent = (hashValue) => new RegExp(`${TOKEN_KEY}=`, 'i').test(hashValue);
/**
 * Parse the given URL `hashValue` for a JWT authentication error
 * @function
 * @param  {String} hashValue - `window.location.hash`
 * @return {Boolean}          - Is there an OpenID-compliant JWT error in the URL hash
 */
export const isAuthHashErrorPresent = (hashValue) => new RegExp(`${ERROR_KEY}=`, 'i').test(hashValue);

/**
 * Decoding JWT value (from base64 -> Object)
 * @function
 * @param  {String} token - The token as returned from the auth server
 * @return {Object}       - The decoded JWT
 */
export const decodeJWT = (token) =>
  token
    .split('.')
    .slice(0, 2)
    .map((val) => JSON.parse(atob(val))) // using browser's base64 encoding (IE 10 compat)
    .reduce((obj, val) => Object.assign({}, val));

/**
 * Processes the openid hash value for the session value
 * @function
 * @param  {String} value - The hash value as set by Auth server redirect
 * @return {Object}       - An object mapping all hash keys to their values
 */
export const processHashValue = (value) => {
  if (!value) return null;

  return value.substr(1).split('&').reduce((values, param) => {
    const [ key, val ] = param.split('=');
    const isToken = key === TOKEN_KEY;
    if (isToken) Object.assign(values, { access_token: val });
    return Object.assign(values, {
      [key]: isToken ? decodeJWT(val) : val,
    });
  }, {});
};

/**
 * Given user session, returns the expiration status
 * @param  {Object} jwt       A deserialized JWT
 * @param  {Date.now} issued  The time the JWT was created
 * @return {Boolean}          Is the JWT expired
 */
const expired = ({ jwt: { iat, exp }, issued }) => exp - iat < (Date.now() - issued) / 1000;

/**
 * Returns the current active deserialized JWT
 */
let cachedValue;
export const getSession = () => {
  if (cachedValue) return cachedValue;
  cachedValue = processHashValue(storage.get(SESSION_STORAGE_KEY));
  return cachedValue || { access_token: null };
};

/**
 * Persist JWT session to browser storage
 * @param  {String} hash - JWT hash
 * @return {Object}      - The deserialized JWT
 */
const persistSession = (hash) => {
  storage.set(SESSION_STORAGE_KEY, hash);
  return getSession();
};
/**
 * Remove the cached JWT from browser storage
 */
export const endSession = () => {
  // Test env doesn't have window or localStorage...
  try {
    window.localStorage.setItem(SESSION_LAST_ACTIVITY, '0');
  } catch (e) {} // eslint-disable-line
  storage.remove(SESSION_STORAGE_KEY);
  cachedValue = null;
};

/**
 * Given a URL hash (window.location.hash), check to see if an authentication flow
 * has completed, if so, persist the session
 * @function
 * @param {string} hash
 */
export const beginAuthSession = (hash) => {
  if (isAuthHashValuePresent(hash)) {
    persistSession(`${hash}&issued=${Date.now()}`);
  } else if (isAuthHashErrorPresent(hash)) {
    persistSession(hash); // currently storing error
  }

  // Remove the query string WITHOUT refreshing the page
  window.history.pushState('Authorized user session', document.title, window.location.pathname);
};

/**
 * Determines if the user's session exists, and if it is still valid
 * @function
 * @return {Boolean} - Is user session valid (true/false)
 */
export const isLoggedIn = () => {
  const session = getSession();
  //.log(session);
  return isNil(session.access_token) ? false : !expired(session);
};

const redirectToIdp = async () => {
  const issuer = process.env.REACT_APP_AUTH_ISSUER;
  const uri = process.env.REACT_APP_AUTH_URI;

  //const params = issuer ? `?issuer=${issuer}` : '';

  const req = new XMLHttpRequest();
  req.open('GET', `${uri}?issuer=${issuer}`, true);

  req.onreadystatechange = () => {
    if (req.readyState < 4) return;
    //console.log(req);
    const { actionUri, samlRequest, stateParam } = JSON.parse(req.responseText);
    const callbackUri = window.location.href;

    // Create a disconnected form
    const $f = document.createElement('form');
    $f.action = actionUri;
    $f.method = 'POST';

    // Create the saml request
    const $samlRequest = document.createElement('input');
    $samlRequest.type = 'hidden';
    $samlRequest.name = 'SAMLRequest';
    $samlRequest.value = samlRequest;
    $f.appendChild($samlRequest);

    // Create the relay state
    const $relayState = document.createElement('input');
    $relayState.type = 'hidden';
    $relayState.name = stateParam;
    $relayState.value = callbackUri;
    $f.appendChild($relayState);

    // Append the form to the body
    document.body.appendChild($f);

    // Go to login
    $f.submit();
  };

  req.send(null);
  /*
  //TODO: add error handling to display error logging in view
  let response = await axios({
    method: 'get',
    url: `${uri}?issuer=${issuer}`,
  });

  const { actionUri, samlRequest, stateParam } = response.data;
  const callBackUri = window.location.href;

  var bodyFormData = new FormData();

  bodyFormData.set('SAMLRequest', samlRequest);
  bodyFormData.set(stateParam, callBackUri);

  //TODO: add error handling to display error logging in view
  axios({
    method: 'post',
    url: actionUri,
    data: bodyFormData,
    config: { headers: { 'Content-Type': 'multipart/form-data' } },
  });
  */
};

/**
 * Determine user's authorization state. Follow authority redirects as necessary
 * @function
 */
export const authorize = () => {
  if (!isLoggedIn()) {
    // no session token available, redirect to IdP
    redirectToIdp();
  }
};

export const SESSION_AUTHORIZE = 'SESSION_AUTHORIZE';
export const SESSION_START = 'SESSION_START';
export const SESSION_END = 'SESSION_END';
export const SESSION_ERROR = 'SESSION_ERROR';

export function login() {
  return {
    type: SESSION_AUTHORIZE,
  };
}
export function start(jwt, access_token) {
  return {
    type: SESSION_START,
    jwt,
    access_token,
  };
}
export function end() {
  return {
    type: SESSION_END,
  };
}
export function error(error) {
  return {
    type: SESSION_ERROR,
    error,
  };
}



// WEBPACK FOOTER //
// ./src/Actions/AuthActions.js